import { cva } from 'class-variance-authority';

/**
 * Empty state class recipes.
 *
 * Like every recipe in this layer these contain ZERO `dark:` variants: colour comes entirely from the
 * `--ds-empty-state-*` slots that `.ds-empty-state` sets in `components/styles/design-system.css`,
 * and both name a semantic role that already carries a value per theme.
 *
 * ── Ported from Figma 2072:139929 on 2026-09-29 ───────────────────────────────────────────────
 *
 * Two variants, one axis (`Size`), and seven component properties. Every number is read off the set
 * rather than chosen, and the spacing is the token scale (`--spacing` is 4px), so nothing here is an
 * arbitrary value:
 *
 *                     inset (v / h)   rhythm   icon   title            description   text cap
 *   inline (480 wide)   32 / 24        16       32    Heading/xs 16    Body/md 14    400
 *   page   (640 wide)   64 / 24        24       48    Heading/lg       Body/lg       480
 *
 * The rhythm is the gap between the icon, the text block and the actions; inside the text block the
 * title and description sit 4px apart inline and 8px on a page. The actions row wraps and centres
 * with a 12px gap on both axes.
 *
 * The 480 / 640 root widths are CANVAS widths — a component set has to be drawn somewhere — so the
 * root is `w-full` and hugs its content vertically; a host constrains it from outside. The text caps
 * (400 / 480) are the exception: they are a measure, not a canvas, so they are kept. They are raw
 * `max-w-[400px]` and `max-w-[480px]` because no spacing step reaches that high, in Figma or in the
 * 20-step scale `noOffScaleSpacing.test.ts` guards; `menuItemDescriptionRecipe` caps its measure the
 * same way. Logged as a DS gap.
 *
 * ── Additions the drawing cannot carry ────────────────────────────────────────────────────────
 *
 * `text-balance` on the title and description, so a long echoed search term or a German sentence
 * does not leave a one-word last line. Figma cannot draw it, so it is an addition and not a
 * reproduction; it changes wrapping only when text wraps, and the drawn one-line cells are identical.
 *
 * `[overflow-wrap:anywhere]` and `min-w-0` on every part, for the reason `ContentBlock.recipe`
 * records: an unbroken token (a URL, a UUID) must not push the block wider than its column.
 *
 * Nothing is padded or aligned with a physical direction, so RTL needs no logical-property spelling.
 * `text-center` is symmetric.
 *
 * ── No motion, deliberately ───────────────────────────────────────────────────────────────────
 *
 * Not one timing utility, which is why this file appears in `motionLiterals.test.ts`'s list of
 * recipes that carry none. The empty state has no interactive state of its own, and it has no
 * open/close lifecycle: a caller mounts and unmounts it, and a recipe cannot tell a first mount from
 * a remount, so an entrance would replay on every back-navigation. A retry spinner is the host's
 * Button `loading`, not this component's motion.
 */

/**
 * The root.
 *
 * `font-sans` because this layer omits Tailwind's preflight, so without it the copy is typeset by
 * whatever sits above it in the tree. The foreground is on the ROOT so the icon (which draws in
 * `currentColor`) and the title inherit it; the description overrides one step back.
 */
export const emptyStateRootRecipe = cva(
  [
    'ds-empty-state',
    'font-sans',
    'flex w-full min-w-0 flex-col items-center text-center',
    'text-[var(--ds-empty-state-fg)]',
  ],
  {
    variants: {
      size: {
        inline: 'gap-4 px-6 py-8',
        page: 'gap-6 px-6 py-16',
      },
    },
    defaultVariants: { size: 'inline' },
  }
);

/** The title and description, stacked, 4px apart inline and 8px on a page, capped at a readable measure. */
export const emptyStateTextRecipe = cva(['flex w-full min-w-0 flex-col items-center'], {
  variants: {
    size: {
      inline: 'max-w-[400px] gap-1',
      page: 'max-w-[480px] gap-2',
    },
  },
  defaultVariants: { size: 'inline' },
});

/**
 * The heading. `m-0` because it is a real `<h1>`–`<h6>` and this layer omits preflight, so the
 * browser's own heading margin would break the rhythm above and below it.
 */
export const emptyStateTitleRecipe = cva(['m-0 min-w-0 text-balance [overflow-wrap:anywhere]'], {
  variants: {
    size: {
      inline: 'text-heading-xs',
      page: 'text-heading-lg',
    },
  },
  defaultVariants: { size: 'inline' },
});

/**
 * The description: a line a user is meant to READ, so it is the secondary foreground (a readable
 * floor), never a decorative grey.
 */
export const emptyStateDescriptionRecipe = cva(
  ['m-0 min-w-0 text-balance text-[var(--ds-empty-state-description)] [overflow-wrap:anywhere]'],
  {
    variants: {
      size: {
        inline: 'text-body-md',
        page: 'text-body-lg',
      },
    },
    defaultVariants: { size: 'inline' },
  }
);

/**
 * The actions: the Figma slot is a wrapping, centred row with a 12px gap on both axes, so two
 * `large` Buttons stack on a 320px phone instead of overflowing or shrinking. Wrapping is additive to
 * the drawing: at the drawn width the row is identical.
 */
export const emptyStateActionsRecipe = cva([
  'flex w-full min-w-0 flex-row flex-wrap items-center justify-center gap-3',
]);
